import * as React from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, MessageBar, MessageBarBody, Spinner, Tab, TabList } from '@fluentui/react-components';
import { type IBatchSummary, summarizeBatches, UndoEngine } from '../../../src/engine/UndoEngine';
import { GraphService } from '../../../src/services/GraphService';
import { errorText, useApp } from '../app/context';
import { batchTitle, describeStep } from '../history/describe';
import { beforeAfter, countNodes } from '../history/tree';
import Tree from '../components/Tree';
import { PRODUCT_NAME } from '../app/product';
import type { RecordSet } from '../records/FirmRecords';
import { filesTouched, refreshSummary } from '../records/stage';

const MAX_TREE: number = 400;

function BeforeAfter(props: { batch: IBatchSummary }): React.ReactElement {
  const { before, after } = React.useMemo(() => beforeAfter(props.batch.operations), [props.batch]);
  if (countNodes(before) + countNodes(after) > MAX_TREE) {
    return <p className="small">Too many items to draw here; the list of steps below shows every one.</p>;
  }
  return (
    <div className="before-after">
      <div>
        <div className="lbl">Before</div>
        {before.children.length ? <Tree node={before} side="before" /> : <p className="small">Nothing.</p>}
      </div>
      <div>
        <div className="lbl">After</div>
        {after.children.length ? <Tree node={after} side="after" /> : <p className="small">Nothing.</p>}
      </div>
    </div>
  );
}

/** "Today", "Yesterday", or "Monday, October 5". */
function dayLabel(iso: string): string {
  const d: Date = new Date(iso);
  const start = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days: number = Math.round((start(new Date()) - start(d)) / 86400000);
  return days === 0 ? 'Today' : days === 1 ? 'Yesterday' : d.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

export default function History(): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const [batches, setBatches] = React.useState<IBatchSummary[] | undefined>(undefined);
  const [confirm, setConfirm] = React.useState<IBatchSummary | undefined>(undefined);
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  // Restricted files keep their own history, open only to the Restricted files group.
  const [which, setWhich] = React.useState<'main' | 'restricted'>('main');
  const set: RecordSet = which === 'restricted' && app.records.restricted ? app.records.restricted : app.records.main;
  const audit = which === 'restricted' ? set.audit : app.audit;
  const library = which === 'restricted' ? set.library : { driveId: w.driveId, libraryName: w.libraryName };

  const load = React.useCallback(() => {
    setError(undefined);
    setBatches(undefined);
    audit.readEntries(library.driveId).then(e => setBatches(summarizeBatches(e))).catch(e => {
      setBatches([]);
      setError(which === 'restricted' && /\b(401|403|404)\b|owner/.test(String((e as Error)?.message)) ? 'Only people in the Restricted files group can see this history.' : errorText(e));
    });
  }, [audit, library.driveId, which]);
  React.useEffect(load, [load]);

  const undo = async (b: IBatchSummary): Promise<void> => {
    setWorking(true);
    try {
      const engine = new UndoEngine({ writer: new GraphService(app.graph).createDriveWriter(library.driveId), audit, libraryId: library.driveId, libraryName: library.libraryName, user: app.account.username, records: set.writer });
      const r = await engine.undo(b);
      // Keep the board's counts and due dates right for the files this touched.
      for (const key of await filesTouched(set, b.operations).catch(() => [] as string[])) {
        await refreshSummary(set, key).catch(() => undefined);
      }
      const failed: number = r.entries.filter(e => e.status === 'failed').length;
      app.toast({ text: r.abortedReason ?? (failed ? `Put back, except ${failed} item${failed === 1 ? '' : 's'} that changed since. The details are in History.` : 'Undone. Everything is back where it was.') });
      setConfirm(undefined);
      load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const days: { label: string; batches: IBatchSummary[] }[] = [];
  for (const b of batches ?? []) {
    const label: string = dayLabel(b.startedAt);
    if (days.length && days[days.length - 1].label === label) {
      days[days.length - 1].batches.push(b);
    } else {
      days.push({ label, batches: [b] });
    }
  }

  return (
    <main className="page">
      <div className="page-head">
        <div>
          <h1>History</h1>
          <p>Every change {PRODUCT_NAME} made, who made it and when. Undo the newest change on a record to put it back.</p>
        </div>
        <a href={audit.viewUrl} target="_blank" rel="noreferrer">Open the full log in SharePoint</a>
      </div>
      {app.records.restricted && (
        <TabList selectedValue={which} onTabSelect={(_, d) => setWhich(d.value as 'main' | 'restricted')}>
          <Tab value="main">Files and records</Tab>
          <Tab value="restricted">Restricted files</Tab>
        </TabList>
      )}
      {which === 'restricted' && <p className="small">Changes to restricted files, and who opened them.</p>}
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {!batches && <Spinner label="Reading History…" />}
      {batches && batches.length === 0 && !error && <p className="muted">Nothing has been changed yet.</p>}
      {days.map(day => (
        <section key={day.label} aria-label={day.label} className="card" style={{ gap: 0, padding: '6px 22px 10px' }}>
          <h2 style={{ margin: '10px 0 4px', fontSize: 15 }}>{day.label}</h2>
          {day.batches.map(b => {
            const isUndo: boolean = b.batchId.indexOf('UNDO-') === 0;
            const steps = b.operations.filter(o => o.action !== 'createFolder' || o.status !== 'success').map(o => ({ o, s: describeStep(o) }));
            const created = b.operations.filter(o => o.action === 'createFolder' && o.status === 'success');
            return (
              <div key={b.batchId} style={{ display: 'flex', gap: 16, padding: '12px 0', borderTop: '1px solid var(--line-2)', alignItems: 'flex-start' }}>
                <span className="muted" style={{ width: 52, flex: 'none', fontSize: 14, paddingTop: 1 }}>{new Date(b.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                <div className="stack" style={{ flex: 1 }}>
                  <span style={{ fontSize: 15 }}>{batchTitle(b.operations, isUndo)}</span>
                  <span className="small">
                    {b.user}
                    {b.failed ? ` · ${b.failed} couldn't be done` : ''}
                    {b.state === 'undone' ? ' · undone since' : b.state === 'partiallyUndone' ? ' · partly undone since' : ''}
                  </span>
                  <details>
                    <summary className="small" style={{ cursor: 'pointer' }}>What changed</summary>
                    <BeforeAfter batch={b} />
                    <ul className="steps-list">
                      {steps.slice(0, 300).map(({ o, s: st }) => (
                        <li key={o.operationId} style={{ color: st.problem ? 'var(--red)' : undefined }}>
                          {st.text}{st.problem ? `. ${st.problem}` : ''}
                        </li>
                      ))}
                      {created.length > 0 && <li className="small">Created {created.length} folder{created.length === 1 ? '' : 's'} to hold them: {created.map(c => c.destinationPath.split('/').join(' › ')).join('; ')}</li>}
                      {steps.length > 300 && <li className="small">and {steps.length - 300} more (all of them are in the full log)</li>}
                    </ul>
                  </details>
                </div>
                {b.canUndo && <Button size="small" onClick={() => setConfirm(b)}>Undo</Button>}
              </div>
            );
          })}
        </section>
      ))}
      <Dialog open={!!confirm} onOpenChange={(_, d) => !d.open && setConfirm(undefined)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Undo this?</DialogTitle>
            <DialogContent>
              {confirm && `${confirm.reversible.filter(e => e.action !== 'createFolder').length} item(s) will be moved back to where they were, with their old names. Folders ${PRODUCT_NAME} created for them are removed once they're empty. Anything someone changed since is left alone and listed here.`}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" disabled={working} onClick={() => { void undo(confirm!); }}>{working ? 'Undoing…' : 'Undo'}</Button>
              <Button disabled={working} onClick={() => setConfirm(undefined)}>Cancel</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </main>
  );
}
